///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { Loader } from "@inweb/viewer-core";

import { Viewer } from "../Viewer";
import { GLTFLoadingManager, GLTFLoadParams } from "./GLTFLoadingManager";
import { ModelImpl } from "../models/ModelImpl";

export class GLTFFileLoader extends Loader {
  public viewer: Viewer;

  constructor(viewer: Viewer) {
    super();
    this.viewer = viewer;
  }

  override isSupport(file: any, format?: string): boolean {
    return (
      (typeof file === "string" || file instanceof globalThis.File || file instanceof ArrayBuffer) &&
      /(gltf|glb)$/i.test(format)
    );
  }

  override async load(file: any, format?: string, params?: GLTFLoadParams): Promise<this> {
    const manager = new GLTFLoadingManager(file, params);

    const loader = new GLTFLoader(manager);
    loader.setPath(manager.path);
    loader.setCrossOrigin(params.crossOrigin || loader.crossOrigin);
    loader.setWithCredentials(params.withCredentials || loader.withCredentials);

    const progress = (event: ProgressEvent) => {
      const { lengthComputable, loaded, total } = event;
      const progress = lengthComputable ? loaded / total : 1;
      this.viewer.emitEvent({ type: "geometryprogress", data: progress, file });
    };

    const gltf = await loader.loadAsync(manager.fileURL, progress);
    if (!this.viewer.scene) return this;

    const modelImpl = new ModelImpl(gltf.scene);
    modelImpl.loader = this;
    modelImpl.viewer = this.viewer;

    this.viewer.scene.add(gltf.scene);
    this.viewer.models.push(modelImpl);

    this.viewer.syncOptions();
    this.viewer.syncOverlay();
    this.viewer.update();

    this.viewer.emitEvent({ type: "databasechunk", data: gltf.scene, file });

    return this;
  }
}
